import React, { useState, useEffect, useRef } from 'react';
import PropTypes from 'prop-types';
import { motion, AnimatePresence } from 'framer-motion';
import { useAuth } from '../../contexts/AuthContext';
import { resolveBackendUrl } from '../../utils/backendUrl';
import { Icons } from '../Icons';
import SchoolAutocomplete from '../ui/SchoolAutocomplete';

const EASE = [0.22, 1, 0.36, 1];
const SANS = '"Geist", "Inter", system-ui, sans-serif';
const MONO = '"Geist Mono", ui-monospace, monospace';
const BRAND   = '#5A38FD';
const BRAND_2 = '#7F00FF';
const BRAND_L = '#a78bfa';
const GRAIN_SVG = `url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='300' height='300'%3E%3Cfilter id='n'%3E%3CfeTurbulence type='fractalNoise' baseFrequency='0.75' numOctaves='4' stitchTiles='stitch'/%3E%3C/filter%3E%3Crect width='300' height='300' filter='url(%23n)' opacity='1'/%3E%3C/svg%3E")`;

// ── Password strength ─────────────────────────────────────────────────────────
const getStrength = (pwd) => {
  if (!pwd) return 0;
  let s = 0;
  if (pwd.length >= 8) s++;
  if (/[A-Z]/.test(pwd)) s++;
  if (/[0-9]/.test(pwd)) s++;
  if (/[^A-Za-z0-9]/.test(pwd)) s++;
  return s;
};

const STRENGTH_META = [
  null,
  { label: 'Weak',   color: '#ef4444' },
  { label: 'Fair',   color: '#f59e0b' },
  { label: 'Good',   color: '#84cc16' },
  { label: 'Strong', color: '#34d399' },
];

function PasswordStrengthBar({ password }) {
  const strength = getStrength(password);
  if (!password) return null;
  const meta = STRENGTH_META[strength] || STRENGTH_META[1];
  return (
    <div style={{ marginTop: 8 }}>
      <div style={{ display: 'flex', gap: 4, marginBottom: 4 }}>
        {[1, 2, 3, 4].map(i => (
          <motion.div key={i}
            animate={{ background: i <= strength ? meta.color : 'rgba(255,255,255,0.08)' }}
            transition={{ duration: 0.25 }}
            style={{ flex: 1, height: 3, borderRadius: 2 }}
          />
        ))}
      </div>
      <p style={{ fontSize: '0.72rem', color: meta.color, fontWeight: 600, margin: 0, fontFamily: MONO }}>
        {meta.label}
        {strength < 3 && <span style={{ color: 'rgba(255,255,255,0.3)', fontWeight: 400 }}> — add uppercase, numbers, or symbols</span>}
      </p>
    </div>
  );
}

// ── Sport options ─────────────────────────────────────────────────────────────
const SPORTS = [
  'Football', 'Basketball', 'Baseball', 'Soccer', 'Tennis',
  'Track & Field', 'Swimming', 'Volleyball', 'Golf', 'Softball', 'Wrestling', 'Other',
];

// ── Left-panel feature highlights ────────────────────────────────────────────
const FEATURES = [
  { icon: Icons.Contract, color: BRAND_L, label: 'Contract Analyzer',  stat: 'Deal Safety Score™',      desc: 'Red flags identified in seconds' },
  { icon: Icons.Star,     color: BRAND_L, label: 'NIL Score',           stat: 'Live leaderboard',        desc: 'Track your brand value & rank' },
  { icon: Icons.Ghost,    color: BRAND_L, label: 'Ghost Agent',         stat: 'Always in your corner',   desc: 'AI negotiation coach' },
];

// ── Shared input style ────────────────────────────────────────────────────────
function Field({ label, hint, children }) {
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
      <label style={{ fontSize: '0.78rem', fontWeight: 600, color: 'rgba(255,255,255,0.5)', display: 'flex', gap: 6, alignItems: 'baseline' }}>
        {label}
        {hint && <span style={{ fontWeight: 400, color: 'rgba(255,255,255,0.22)', fontSize: '0.72rem' }}>{hint}</span>}
      </label>
      {children}
    </div>
  );
}

function Input({ type = 'text', name, value, onChange, placeholder, required, autoComplete }) {
  const [focused, setFocused] = useState(false);
  return (
    <input
      type={type} name={name} value={value} onChange={onChange}
      placeholder={placeholder} required={required} autoComplete={autoComplete}
      onFocus={() => setFocused(true)} onBlur={() => setFocused(false)}
      style={{
        width: '100%', boxSizing: 'border-box',
        padding: '10px 14px', borderRadius: 10,
        background: focused ? 'rgba(90,56,253,0.07)' : 'rgba(255,255,255,0.04)',
        border: `1px solid ${focused ? 'rgba(90,56,253,0.55)' : 'rgba(255,255,255,0.09)'}`,
        boxShadow: focused ? '0 0 0 3px rgba(90,56,253,0.12)' : 'none',
        color: '#fff', fontSize: '0.9rem', outline: 'none',
        transition: 'border-color 160ms, background 160ms, box-shadow 160ms',
        fontFamily: SANS,
      }}
    />
  );
}

function SelectInput({ name, value, onChange, required, children }) {
  const [focused, setFocused] = useState(false);
  return (
    <div style={{ position: 'relative' }}>
      <select
        name={name} value={value} onChange={onChange} required={required}
        onFocus={() => setFocused(true)} onBlur={() => setFocused(false)}
        style={{
          width: '100%', boxSizing: 'border-box', appearance: 'none', WebkitAppearance: 'none',
          padding: '10px 36px 10px 14px', borderRadius: 10,
          background: focused ? 'rgba(90,56,253,0.07)' : 'rgba(255,255,255,0.04)',
          border: `1px solid ${focused ? 'rgba(90,56,253,0.55)' : 'rgba(255,255,255,0.09)'}`,
          boxShadow: focused ? '0 0 0 3px rgba(90,56,253,0.12)' : 'none',
          color: value ? '#fff' : 'rgba(255,255,255,0.28)',
          fontSize: '0.9rem', outline: 'none', cursor: 'pointer',
          transition: 'border-color 160ms, background 160ms, box-shadow 160ms',
          fontFamily: SANS,
        }}
      >
        {children}
      </select>
      <div style={{ position: 'absolute', right: 12, top: '50%', transform: 'translateY(-50%)', pointerEvents: 'none', color: 'rgba(255,255,255,0.28)' }}>
        <Icons.ChevronDown style={{ width: 16, height: 16 }} />
      </div>
    </div>
  );
}

// ── AuthScreen ────────────────────────────────────────────────────────────────
const AuthScreen = ({ onBack, defaultTab = 'signin', pendingPlan = null }) => {
  const { signUp, signIn, loading: authLoading } = useAuth();
  const backendUrl = resolveBackendUrl();
  const backendInvalid = !backendUrl || /none\.preview|undefined|null|^https?:\/\/$/i.test(String(backendUrl || ''));

  const [isLogin, setIsLogin] = useState(defaultTab !== 'signup');
  const [signupExpanded, setSignupExpanded] = useState(defaultTab === 'signup');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [resetSent, setResetSent] = useState(false);

  useEffect(() => { window.scrollTo({ top: 0, behavior: 'instant' }); }, []);

  const [formData, setFormData] = useState({
    email: '', password: '', first_name: '', last_name: '', sport: '', school: '',
  });

  const handleInputChange = (e) => {
    setFormData(prev => ({ ...prev, [e.target.name]: e.target.value }));
    setError('');
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');
    try {
      let result;
      if (isLogin) {
        result = await signIn(formData.email, formData.password);
      } else {
        result = await signUp(formData.email, formData.password, {
          first_name: formData.first_name,
          last_name: formData.last_name,
          sport: formData.sport,
          school: formData.school,
        });
      }
      if (result?.error) {
        setError(result.error.message);
      } else if (result?.success) {
        const plan = !isLogin ? pendingPlan : null;
        window.location.href = isLogin
          ? '/?skipTutorial=1'
          : `/?skipTutorial=1&newSignup=1&promptInstagram=1${plan ? `&plan=${plan}` : ''}`;
      } else {
        setError('Authentication failed. Please try again.');
      }
    } catch (err) {
      setError(err.message || 'Authentication failed');
    } finally {
      setLoading(false);
    }
  };

  const handleForgotPassword = async () => {
    if (!formData.email) { setError('Enter your email first.'); return; }
    setLoading(true);
    try {
      const res = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/auth/request-password-reset`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email: formData.email }),
      });
      if (!res.ok) throw new Error('Failed');
      setResetSent(true);
      setError('');
    } catch {
      setError('Could not send reset email. Try again later.');
    } finally {
      setLoading(false);
    }
  };

  const switchTab = (toLogin) => {
    setIsLogin(toLogin);
    setSignupExpanded(false);
    setError('');
    setResetSent(false);
  };

  if (authLoading) {
    return (
      <div style={{ minHeight: '100vh', background: '#08080D', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        <motion.div animate={{ rotate: 360 }} transition={{ repeat: Infinity, duration: 0.9, ease: 'linear' }}
          style={{ width: 28, height: 28, borderRadius: '50%', border: `2px solid rgba(90,56,253,0.2)`, borderTopColor: BRAND }} />
      </div>
    );
  }

  return (
    <div style={{ minHeight: '100vh', background: '#08080D', fontFamily: SANS, color: '#fff', overflowX: 'hidden' }}>
      <style>{`
        .auth-two-col { display: grid; grid-template-columns: 1fr 1fr; min-height: 100vh; }
        @media (max-width: 860px) { .auth-two-col { grid-template-columns: 1fr; } .auth-left-panel { display: none !important; } }
        ::placeholder { color: rgba(255,255,255,0.2) !important; }
        select option { background: #141421; color: #fff; }
      `}</style>

      <div className="auth-two-col">

        {/* ── Left panel — branding ── */}
        <div className="auth-left-panel" style={{
          position: 'sticky', top: 0, height: '100vh', overflow: 'hidden',
          background: 'linear-gradient(160deg, #0D0B1A 0%, #08080D 60%)',
          borderRight: '1px solid rgba(255,255,255,0.05)',
          display: 'flex', flexDirection: 'column', justifyContent: 'center',
          padding: '60px 52px',
        }}>
          {/* Grain */}
          <div style={{ position: 'absolute', inset: 0, opacity: 0.04, mixBlendMode: 'overlay', backgroundImage: GRAIN_SVG, backgroundSize: '120px 120px', pointerEvents: 'none' }} />
          {/* Orbs */}
          <motion.div animate={{ scale: [1, 1.1, 1], opacity: [0.12, 0.2, 0.12] }} transition={{ repeat: Infinity, duration: 8, ease: 'easeInOut' }}
            style={{ position: 'absolute', top: -180, left: -100, width: 500, height: 500, background: `radial-gradient(circle, rgba(90,56,253,0.22) 0%, transparent 70%)`, borderRadius: '50%', filter: 'blur(60px)', pointerEvents: 'none' }} />
          <motion.div animate={{ scale: [1, 1.08, 1], opacity: [0.08, 0.14, 0.08] }} transition={{ repeat: Infinity, duration: 11, ease: 'easeInOut', delay: 3 }}
            style={{ position: 'absolute', bottom: -100, right: -80, width: 380, height: 380, background: `radial-gradient(circle, rgba(127,0,255,0.18) 0%, transparent 70%)`, borderRadius: '50%', filter: 'blur(50px)', pointerEvents: 'none' }} />

          <div style={{ position: 'relative', zIndex: 1 }}>
            {/* Logo */}
            <motion.div initial={{ opacity: 0, y: -12 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.55, ease: EASE }}
              style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 56 }}>
              <img src="/nilvision-logo-gradient-background.png" alt="NILVision" style={{ width: 36, height: 36, borderRadius: 10 }} />
              <span style={{ fontSize: '1.15rem', fontWeight: 800, letterSpacing: '-0.02em', background: 'linear-gradient(135deg, #fff 0%, #e0d7ff 55%, #c4b5fd 100%)', WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent', backgroundClip: 'text' }}>NILVISION</span>
            </motion.div>

            {/* Headline */}
            <motion.div initial={{ opacity: 0, y: 18 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.65, ease: EASE, delay: 0.08 }}>
              <h2 style={{ fontSize: 'clamp(1.8rem, 3vw, 2.4rem)', fontWeight: 900, letterSpacing: '-0.035em', lineHeight: 1.08, margin: '0 0 14px',
                background: 'linear-gradient(150deg, #fff 0%, #e0d7ff 50%, #c4b5fd 100%)',
                WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent', backgroundClip: 'text' }}>
                Your AI NIL Agent.<br />Built to get you paid, safely.
              </h2>
              <p style={{ fontSize: '0.95rem', color: 'rgba(255,255,255,0.35)', lineHeight: 1.65, margin: '0 0 52px', maxWidth: 340 }}>
                Contract analysis, negotiation strategy, earnings tracking, and social compliance — all in one place.
              </p>
            </motion.div>

            {/* Feature highlights */}
            <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
              {FEATURES.map((f, i) => (
                <motion.div key={f.label}
                  initial={{ opacity: 0, x: -16 }} animate={{ opacity: 1, x: 0 }}
                  transition={{ duration: 0.5, ease: EASE, delay: 0.18 + i * 0.09 }}
                  style={{ display: 'flex', alignItems: 'center', gap: 14, padding: '12px 14px', borderRadius: 14,
                    background: 'rgba(255,255,255,0.03)', border: '1px solid rgba(255,255,255,0.06)' }}>
                  <div style={{ width: 40, height: 40, borderRadius: 11, flexShrink: 0,
                    background: `color-mix(in srgb, ${f.color} 14%, rgba(0,0,0,0))`,
                    border: `1px solid color-mix(in srgb, ${f.color} 22%, transparent)`,
                    display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                    <f.icon style={{ width: 18, height: 18, color: f.color }} />
                  </div>
                  <div style={{ flex: 1 }}>
                    <div style={{ fontSize: '0.85rem', fontWeight: 700, color: 'rgba(255,255,255,0.8)', marginBottom: 2 }}>{f.label}</div>
                    <div style={{ fontSize: '0.75rem', color: 'rgba(255,255,255,0.28)' }}>{f.desc}</div>
                  </div>
                  <div style={{ fontSize: '0.6rem', fontWeight: 700, color: f.color, background: `color-mix(in srgb, ${f.color} 12%, rgba(0,0,0,0))`,
                    border: `1px solid color-mix(in srgb, ${f.color} 22%, transparent)`,
                    borderRadius: 999, padding: '3px 10px', whiteSpace: 'nowrap', fontFamily: MONO, letterSpacing: '0.03em' }}>
                    {f.stat}
                  </div>
                </motion.div>
              ))}
            </div>

            {/* Trust footnote */}
            <motion.p initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 0.55, duration: 0.5 }}
              style={{ marginTop: 44, fontSize: '0.7rem', color: 'rgba(255,255,255,0.15)', fontFamily: MONO, lineHeight: 1.6 }}>
              Bank-level encryption · Official Instagram API · Built by former student-athletes
            </motion.p>
          </div>
        </div>

        {/* ── Right panel — form ── */}
        <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', padding: '48px 24px 80px', minHeight: '100vh', overflowY: 'auto' }}>
          <div style={{ width: '100%', maxWidth: 420 }}>

            {/* Mobile logo */}
            <div style={{ display: 'none' }} className="auth-mobile-logo">
              <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 32 }}>
                <img src="/nilvision-logo-gradient-background.png" alt="NILVision" style={{ width: 32, height: 32, borderRadius: 8 }} />
                <span style={{ fontSize: '1.1rem', fontWeight: 800, background: 'linear-gradient(135deg, #fff, #c4b5fd)', WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent', backgroundClip: 'text' }}>NILVISION</span>
              </div>
            </div>

            {/* Back link */}
            {onBack && (
              <motion.button initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ duration: 0.4 }}
                onClick={onBack}
                style={{ display: 'flex', alignItems: 'center', gap: 6, background: 'none', border: 'none', cursor: 'pointer', color: 'rgba(255,255,255,0.3)', fontSize: '0.8rem', padding: 0, marginBottom: 28, fontFamily: SANS, transition: 'color 150ms' }}
                onMouseEnter={e => e.currentTarget.style.color = 'rgba(255,255,255,0.6)'}
                onMouseLeave={e => e.currentTarget.style.color = 'rgba(255,255,255,0.3)'}>
                <Icons.ChevronLeft style={{ width: 15, height: 15 }} />
                Back to Home
              </motion.button>
            )}

            {/* Headline */}
            <motion.div initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.5, ease: EASE }}
              style={{ marginBottom: 28 }}>
              <AnimatePresence mode="wait">
                <motion.div key={isLogin ? 'signin-head' : 'signup-head'}
                  initial={{ opacity: 0, y: 6 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: -6 }}
                  transition={{ duration: 0.22, ease: EASE }}>
                  <h1 style={{ fontSize: 'clamp(1.5rem, 4vw, 1.9rem)', fontWeight: 800, letterSpacing: '-0.03em', margin: '0 0 6px',
                    background: 'linear-gradient(135deg, #fff 0%, #e0d7ff 55%, #c4b5fd 100%)',
                    WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent', backgroundClip: 'text' }}>
                    {isLogin ? 'Welcome back' : 'Your NIL career starts here'}
                  </h1>
                  <p style={{ margin: 0, color: 'rgba(255,255,255,0.3)', fontSize: '0.875rem' }}>
                    {isLogin ? 'Sign in to your NILVision account' : 'Free to start — no credit card required'}
                  </p>
                </motion.div>
              </AnimatePresence>
            </motion.div>

            {/* Tab toggle */}
            <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 0.1, duration: 0.4 }}
              style={{ display: 'flex', gap: 4, background: 'rgba(255,255,255,0.04)', border: '1px solid rgba(255,255,255,0.07)', borderRadius: 12, padding: 4, marginBottom: 24 }}>
              {[{ login: true, label: 'Sign In' }, { login: false, label: 'Create Account' }].map(t => (
                <motion.button key={String(t.login)} whileTap={{ scale: 0.97 }} onClick={() => switchTab(t.login)}
                  style={{
                    flex: 1, padding: '9px 0', borderRadius: 9, border: 'none', cursor: 'pointer',
                    fontSize: '0.875rem', fontWeight: 600, fontFamily: SANS,
                    background: isLogin === t.login ? `linear-gradient(135deg, ${BRAND}, ${BRAND_2})` : 'transparent',
                    color: isLogin === t.login ? '#fff' : 'rgba(255,255,255,0.3)',
                    boxShadow: isLogin === t.login ? '0 2px 10px rgba(90,56,253,0.4)' : 'none',
                    transition: 'color 180ms, box-shadow 180ms',
                  }}>
                  {t.label}
                </motion.button>
              ))}
            </motion.div>

            {/* Error / success messages */}
            <AnimatePresence>
              {backendInvalid && (
                <motion.div initial={{ opacity: 0, y: -6 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0 }}
                  style={{ background: 'rgba(239,68,68,0.1)', border: '1px solid rgba(239,68,68,0.25)', borderRadius: 10, padding: '10px 14px', marginBottom: 16, fontSize: '0.8rem', color: '#fca5a5' }}>
                  App not configured with a backend URL. Set REACT_APP_BACKEND_URL and redeploy.
                </motion.div>
              )}
              {error && (
                <motion.div key="err" initial={{ opacity: 0, y: -6 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0 }}
                  style={{ background: 'rgba(239,68,68,0.1)', border: '1px solid rgba(239,68,68,0.25)', borderRadius: 10, padding: '10px 14px', marginBottom: 16, fontSize: '0.8rem', color: '#fca5a5' }}>
                  {error}
                </motion.div>
              )}
              {resetSent && (
                <motion.div key="reset" initial={{ opacity: 0, y: -6 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0 }}
                  style={{ background: 'rgba(90,56,253,0.08)', border: '1px solid rgba(90,56,253,0.2)', borderRadius: 10, padding: '10px 14px', marginBottom: 16, fontSize: '0.8rem', color: BRAND_L }}>
                  If that email exists, a temporary password has been sent.
                </motion.div>
              )}
            </AnimatePresence>

            {/* Form */}
            <form onSubmit={handleSubmit} style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
              <AnimatePresence initial={false}>
                {!isLogin && (
                  <motion.div key="signup-fields"
                    initial={{ opacity: 0, height: 0 }} animate={{ opacity: 1, height: 'auto' }} exit={{ opacity: 0, height: 0 }}
                    transition={{ duration: 0.3, ease: EASE }}
                    onAnimationComplete={() => setSignupExpanded(!isLogin)}
                    style={{ overflow: signupExpanded ? 'visible' : 'hidden', display: 'flex', flexDirection: 'column', gap: 14 }}>

                    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10 }}>
                      <Field label="First Name">
                        <Input name="first_name" value={formData.first_name} onChange={handleInputChange} placeholder="First" required autoComplete="given-name" />
                      </Field>
                      <Field label="Last Name">
                        <Input name="last_name" value={formData.last_name} onChange={handleInputChange} placeholder="Last" required autoComplete="family-name" />
                      </Field>
                    </div>

                    <Field label="Your Sport" hint="— personalizes your NIL Score">
                      <SelectInput name="sport" value={formData.sport} onChange={handleInputChange} required>
                        <option value="">Select your sport…</option>
                        {SPORTS.map(s => <option key={s} value={s}>{s}</option>)}
                      </SelectInput>
                    </Field>

                    <Field label="School">
                      <SchoolAutocomplete
                        value={formData.school}
                        onChange={(school) => setFormData(prev => ({ ...prev, school }))}
                        placeholder="Search for your school…"
                      />
                    </Field>
                  </motion.div>
                )}
              </AnimatePresence>

              <Field label="Email">
                <Input type="email" name="email" value={formData.email} onChange={handleInputChange} placeholder="you@school.edu" required autoComplete="email" />
              </Field>

              <Field label="Password">
                <Input type="password" name="password" value={formData.password} onChange={handleInputChange} placeholder="••••••••" required autoComplete={isLogin ? 'current-password' : 'new-password'} />
                {!isLogin && <PasswordStrengthBar password={formData.password} />}
              </Field>

              {isLogin && (
                <div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: -6 }}>
                  <button type="button" onClick={handleForgotPassword}
                    style={{ background: 'none', border: 'none', cursor: 'pointer', color: BRAND_L, fontSize: '0.78rem', padding: 0, fontFamily: SANS, transition: 'color 150ms' }}
                    onMouseEnter={e => e.currentTarget.style.color = '#fff'}
                    onMouseLeave={e => e.currentTarget.style.color = BRAND_L}>
                    Forgot password?
                  </button>
                </div>
              )}

              {/* Submit */}
              <motion.button type="submit" disabled={loading} whileTap={{ scale: 0.97 }}
                style={{
                  position: 'relative', overflow: 'hidden', marginTop: 4,
                  padding: '12px 0', borderRadius: 12, border: 'none', cursor: loading ? 'not-allowed' : 'pointer',
                  width: '100%', fontWeight: 700, fontSize: '0.95rem', color: '#fff', fontFamily: SANS,
                  background: loading ? 'rgba(90,56,253,0.5)' : `linear-gradient(135deg, ${BRAND}, ${BRAND_2})`,
                  boxShadow: loading ? 'none' : '0 4px 18px rgba(90,56,253,0.4)',
                  transition: 'box-shadow 180ms, background 180ms',
                }}>
                {loading ? (
                  <span style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8 }}>
                    <motion.div animate={{ rotate: 360 }} transition={{ repeat: Infinity, duration: 0.9, ease: 'linear' }}
                      style={{ width: 16, height: 16, borderRadius: '50%', border: '2px solid rgba(255,255,255,0.25)', borderTopColor: '#fff' }} />
                    Processing…
                  </span>
                ) : (
                  isLogin ? 'Sign In' : 'Create Your Free Account'
                )}
              </motion.button>
            </form>

            {/* Legal */}
            <p style={{ marginTop: 18, textAlign: 'center', fontSize: '0.68rem', color: 'rgba(255,255,255,0.18)', lineHeight: 1.6, fontFamily: MONO }}>
              By {isLogin ? 'signing in' : 'creating an account'} you agree to our{' '}
              <a href="/legal/terms" target="_blank" rel="noopener noreferrer" style={{ color: BRAND_L, textDecoration: 'none' }}>Terms</a>
              {' '}and{' '}
              <a href="/legal/privacy" target="_blank" rel="noopener noreferrer" style={{ color: BRAND_L, textDecoration: 'none' }}>Privacy Policy</a>
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

AuthScreen.propTypes = {
  onBack: PropTypes.func,
  defaultTab: PropTypes.oneOf(['signin', 'signup']),
};

export default AuthScreen;
